/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../api.js";
import _ from "underscore";
import util from "../util/util.js";
import h from "util/dom-builder.js";
import * as eventUtil from "../util/event-util.js";
import * as utmGrid from "../map/utm-grid.js";
import googleMaps from '../util/google-maps.js';

import '../ui/quiz.js';

var EventViewModel = function(params) {
	var self = this;

	// Set default params
	params = _.defaults(params, {
	});

	self.me = ko.observable();
	birdata.me().then(self.me);


	// Determine event code either from query params or URL structure
	function getEventCode() {
		var queryParams = util.queryParams();
		if(queryParams.event) {
			return queryParams.event;
		}
		var m = window.location.pathname.match(/\/event\/([^\/]+)$/);
		if(m) {
			return m[1];
		}
	}

	var eventCode = getEventCode();

	self.eventCode = eventCode;
	self.event = ko.observable();
	self.program = ko.observable();
	self.dates = ko.observable();
	self.loaded = ko.observable(false);
	self.error = ko.observable();
	self.quizEnabled = ko.observable();
	self.passedQuiz = ko.observable();
	self.bookingsOpen = ko.observable();
	self.eventFinished = ko.observable();
	self.bookingCode = ko.observable('');
	self.bookingCodeStatus = ko.observable('none');
	self.bookings = ko.observable();
	self.bookingsTitle = ko.pureComputed(function() {
		var count = (self.bookings() || []).length;
		if(count == 0) {
			return "My Bookings";
		} else {
			return "My Bookings (" + count + ")";
		}
	});
	self.maxBookingsText = ko.pureComputed(function() {
		var event = self.event();
		if(event && event.max_bookings_per_person_per_day) {
			var n = event.max_bookings_per_person_per_day;
			return "Note: You may book a maximum of " + util.formatQuantity(n, "site") + " per day";
		}
	});

	self.canJoinBooking = ko.pureComputed(function() {
		return self.bookingCodeStatus() == 'valid';
	});

	self.bookingCode.subscribe(function(code) {
		setTimeout(function() {
			checkBookingCode(self.bookingCode());
		}, 400);
	});

	function checkBookingCode(code) {
		if(code.length == 5) {
			self.bookingCodeStatus('checking');
			birdata.eventBooking(code).then(function() {
				if(code == self.bookingCode()) {
					self.bookingCodeStatus('valid')
				}
			}).catch(function(error) {
				if(code == self.bookingCode()) {
					if(error.xhr && error.xhr.status == 404) {
						self.bookingCodeStatus('invalid');
					} else {
						self.bookingCodeStatus('error');
					}
				}
			})
		} else {
			self.bookingCodeStatus('none');
		}
	}

	self.bookSiteLink = "/booking-map?event=" + eventCode;

	self.registrationStatus = ko.observable();

	function bookingModel(json) {
		return {
			siteName: json.site_name,
			formattedDate: util.formatDate(json.date),
			bookingCode: json.booking_code,
			members: json.members.map(function(m) {
				var name = m.full_name;
				if(m.id == json.creator_id) {
					name += " (organiser)";
				}
				return name;
			}).join(", "),
			mapLink: '/booking-map?' + util.encodeParams({
				event: eventCode,
				site: json.cell_id || json.site_id
			}),
			emailBookingCode: function() {
				self.emailBookingCode(json);
			},
			cancelButtonText: ko.pureComputed(function() {
				var isOrganiser = self.me() && (json.creator_id == self.me().id);
				return isOrganiser ? 'Cancel Booking' : 'Leave Booking';
			}),
			cancel: function() {
				var isOrganiser = self.me() && (json.creator_id == self.me().id);
				var message;
				if(json.members.length == 1) {
					message = "Are you sure you wish to cancel this booking?";
				} else if(isOrganiser) {
					message = "Are you sure you wish to cancel this group booking? This will cancel the booking for all users at this site.";
				} else {
					message = "Are you sure you wish to remove yourself from this group booking?";
				}
				if(window.confirm(message)) {
					birdata.cancelBooking(self.event().id, json.id).then(function() {
						window.location.reload();
					}).catch(function(error) {
						console.log(error);
						alert('Failed to cancel booking. Please contact birdata@birdlife.org.au for assistance.');
					});
				}
			}
		}
	}

	if(eventCode) {
		Promise.all([
			birdata.event(eventCode),
			birdata.lookup()])
		.then(function([event, lookup]) {
			self.program(lookup.find('program', event.program));
			self.dates(util.formatDate(event.start_date) + " to " + util.formatDate(event.finish_date));
			self.registrationStatus(event.participation.has_registered ? 'registered' : 'unregistered');
			self.quizEnabled(event.quiz);
			self.passedQuiz(event.participation.passed_quiz);
			self.bookingsOpen(event.bookings_open);
			self.eventFinished(event.finished);
			self.event(event);
			self.bookings(event.participation.bookings.map(bookingModel));
			self.loaded(true);
		}).catch(function(error) {
			console.log(error);
			self.error("Error loading event information");
		});
	} else {
		window.location.href = '/';
	}

	self.canRegister = ko.observable(true);

	self.register = function() {
		eventUtil.showRegistrationPopup(self.event(), function() {
			self.registrationStatus('updating');
			birdata.registerForEvent(self.event()).then(function() {
				self.registrationStatus('registered');
			}).catch(function() {
				self.registrationStatus('error');
			})
		});
	}

	self.unregister = function() {
		eventUtil.showCancelRegistrationPopup(self.event(), function() {
			birdata.unregisterForEvent(self.event()).then(function() {
				self.registrationStatus('unregistered');
				window.location.reload();
			}).catch(function() {
				self.registrationStatus('error');
			});
		});
	}

	self.takeQuiz = function() {
		var quiz = self.event().quiz;

		if(quiz.endsWith("_external")) {
			eventUtil.showExternalQuizPopup(self.event(), function() {
				birdata.passedQuiz(quiz).then(self.passedQuiz).catch(e => console.log(e));
			});
		} else {
			var div = h('div', { 'class': 'birdata-embed' }, h('div', { 'class': 'modal', 'data-bind': 'component: { name: "quiz", params: quizParams }' }));
			document.body.appendChild(div);

			ko.applyBindings({
				quizParams: {
					quiz: quiz,
					onCompletion: function() {
						div.parentNode.removeChild(div);
						birdata.passedQuiz(quiz).then(self.passedQuiz).catch(e => console.log(e));
					}
				}
			}, div);
		}
	}

	self.joinBooking = function() {
		window.location = eventUtil.joinBookingURL(self.bookingCode());
	}

	self.emailBookingCode = function(booking) {
		var googleMapsURL;
		try {
			googleMapsURL = googleMaps.googleMapsURL(booking.site_location.lat, booking.site_location.lon);
		} catch(error) {}

		var uri = eventUtil.bookingCodeEmailURI({
			eventName: self.event().name,
			bookingDate: booking.date,
			siteName: booking.site_name,
			bookingCode: booking.booking_code,
			googleMapsURL: googleMapsURL
		});

		window.open(uri, '_blank');
	}

	// Force uppercase
	self.bookingCodeInputHandler = function(o, evt) {
		setTimeout(function() {
			var input = evt.target;
			var value = input.value;
			var parts = [
				value.substring(0, input.selectionStart),
				value.substring(input.selectionStart, input.selectionEnd),
				value.substring(input.selectionEnd)
			];

			parts = parts.map(x => x.toUpperCase());

			input.value = parts.join("");
			input.selectionStart = parts[0].length;
			input.selectionEnd = parts[0].length + parts[1].length;
		})
		return true;
	}
};

ko.components.register('event', {
	viewModel: function(params) {
		return new EventViewModel(params);
	},
	template: require('../../templates/event.html')
});

export default EventViewModel;
